import { useState } from "react";
import { Link } from "wouter";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { useConvexUser } from "@/hooks/useConvexUser";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Stethoscope,
  LayoutDashboard,
  FileText,
  Globe,
  MessageSquare,
  Settings as SettingsIcon,
  Users,
  Loader2,
  Copy,
  Link2,
  Mail,
  Trash2,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";

type Role = "admin" | "member";
type AccessStatus = "active" | "locked" | "suspended" | "inactive";

const STATUS_LABEL: Record<AccessStatus, string> = {
  active: "Active",
  locked: "Locked",
  suspended: "Suspended",
  inactive: "Inactive",
};

const STATUS_VARIANT: Record<AccessStatus, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  locked: "outline",
  suspended: "destructive",
  inactive: "secondary",
};

function NavHeader() {
  return (
    <header className="border-b border-border bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[26px]">
        <Link href="/dashboard" className="flex items-center gap-2" data-testid="link-home">
          <Stethoscope className="h-6 w-6 text-primary" />
          <span className="font-bold text-lg leading-none">LRScribe</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm" className="h-6 min-h-0 py-0" data-testid="link-dashboard">
              <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
            </Button>
          </Link>
          <Link href="/templates">
            <Button variant="ghost" size="sm" className="h-6 min-h-0 py-0" data-testid="link-templates">
              <FileText className="h-4 w-4 mr-2" /> Templates
            </Button>
          </Link>
          <Link href="/rules">
            <Button variant="ghost" size="sm" className="h-6 min-h-0 py-0" data-testid="link-rules">
              <Globe className="h-4 w-4 mr-2" /> Rules
            </Button>
          </Link>
          <Link href="/chat">
            <Button variant="ghost" size="sm" className="h-6 min-h-0 py-0" data-testid="link-chat">
              <MessageSquare className="h-4 w-4 mr-2" /> Chat
            </Button>
          </Link>
          <Link href="/team">
            <Button variant="secondary" size="sm" className="h-6 min-h-0 py-0" data-testid="link-team">
              <Users className="h-4 w-4 mr-2" /> Team
            </Button>
          </Link>
          <Link href="/settings">
            <Button variant="ghost" size="sm" className="h-6 min-h-0 py-0" data-testid="link-settings">
              <SettingsIcon className="h-4 w-4 mr-2" /> Settings
            </Button>
          </Link>
        </nav>
        <ThemeToggle className="h-6 w-6 min-h-0 p-0" />
      </div>
    </header>
  );
}

function UpgradeCard({ userId }: { userId: Id<"users"> }) {
  const { toast } = useToast();
  const createTeam = useMutation(api.teams.createTeam);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({ title: "Team name required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await createTeam({ userId, name: name.trim() });
      toast({ title: "Team created", description: "You're now the owner." });
    } catch (err: any) {
      toast({
        title: "Couldn't create team",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="card-upgrade">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" /> Upgrade to Teams
        </CardTitle>
        <CardDescription>
          Create a team to invite colleagues, manage roles, and securely share
          recording sessions. Free to start.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="team-name">Team name</Label>
          <Input
            id="team-name"
            data-testid="input-team-name"
            placeholder="e.g. Riverside Clinic"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <Button onClick={handleCreate} disabled={busy} data-testid="button-create-team">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Create team
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Team() {
  const { authLoading, userId } = useConvexUser();
  const { toast } = useToast();

  const ctx = useQuery(
    api.teams.getTeamContext,
    userId ? { userId } : "skip",
  );
  const members = useQuery(
    api.teams.listMembers,
    userId && ctx?.accountType === "team" ? { userId } : "skip",
  );
  const invitations = useQuery(
    api.teams.listInvitations,
    userId && ctx?.accountType === "team" && ctx.isAdmin ? { userId } : "skip",
  );

  const inviteByEmail = useMutation(api.teams.inviteByEmail);
  const revokeInvitation = useMutation(api.teams.revokeInvitation);
  const setMemberRole = useMutation(api.teams.setMemberRole);
  const setMemberAccessStatus = useMutation(api.teams.setMemberAccessStatus);
  const removeMember = useMutation(api.teams.removeMember);
  const updateTeamSettings = useMutation(api.teams.updateTeamSettings);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("member");
  const [inviting, setInviting] = useState(false);

  if (authLoading || ctx === undefined || (userId && ctx === undefined)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const inviteLink = ctx?.accountType === "team" && ctx.team.inviteCodeEnabled
    ? `${window.location.origin}/join?code=${ctx.team.inviteCode}`
    : "";

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${label} copied` });
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.includes("@")) {
      toast({ title: "Enter a valid email", variant: "destructive" });
      return;
    }
    setInviting(true);
    try {
      const result = await inviteByEmail({
        userId,
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      const link = `${window.location.origin}/join?token=${result.token}`;
      // Best-effort email delivery; fall back to a copyable link.
      let sent = false;
      try {
        const resp = await fetch("/api/team/send-invite-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            email: result.email,
            link,
            teamName: ctx?.accountType === "team" ? ctx.team.name : "",
            role: inviteRole,
          }),
        });
        const data = await resp.json();
        sent = !!data.sent;
      } catch {
        sent = false;
      }
      if (sent) {
        toast({
          title: "Invitation sent",
          description: `An email was sent to ${result.email}.`,
        });
      } else {
        await copy(link, "Invite link");
        toast({
          title: "Invite created",
          description:
            "Email isn't configured, so the invite link was copied — share it with your colleague.",
        });
      }
      setInviteEmail("");
    } catch (err: any) {
      toast({
        title: "Couldn't invite",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setInviting(false);
    }
  };

  const guardedAction = async (fn: () => Promise<unknown>, errTitle: string) => {
    try {
      await fn();
    } catch (err: any) {
      toast({
        title: errTitle,
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Team - LRScribe" description="Manage your LRScribe team, roles, and invitations." />
      <NavHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="text-team-title">
            <Users className="h-6 w-6 text-primary" /> Team
          </h1>
          <p className="text-muted-foreground mt-1">
            Invite colleagues, manage roles and access, and share sessions.
          </p>
        </div>

        {ctx?.accountType !== "team" ? (
          <UpgradeCard userId={userId} />
        ) : (
          <>
            {/* Overview */}
            <Card data-testid="card-team-overview">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle data-testid="text-team-name">{ctx.team.name}</CardTitle>
                    <CardDescription>
                      Your role: <span className="capitalize font-medium">{ctx.role}</span>
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" data-testid="badge-plan">
                    {ctx.team.plan === "free" ? "Free plan" : ctx.team.plan}
                  </Badge>
                </div>
              </CardHeader>
            </Card>

            {/* Invites */}
            {ctx.canInvite && (
              <Card data-testid="card-invite">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="h-5 w-5" /> Invite people
                  </CardTitle>
                  <CardDescription>
                    Send an email invite, or share the link/code below.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      data-testid="input-invite-email"
                      placeholder="colleague@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="flex-1"
                    />
                    {ctx.isAdmin && (
                      <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as Role)}>
                        <SelectTrigger className="w-full sm:w-36" data-testid="select-invite-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button onClick={handleInvite} disabled={inviting} data-testid="button-send-invite">
                      {inviting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Invite
                    </Button>
                  </div>

                  {inviteLink && (
                    <div className="flex items-center gap-2 rounded-md border border-border p-3 bg-muted/40">
                      <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <code className="text-sm truncate flex-1" data-testid="text-invite-link">
                        {inviteLink}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copy(inviteLink, "Invite link")}
                        data-testid="button-copy-link"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {ctx.team.inviteCodeEnabled && (
                    <p className="text-sm text-muted-foreground">
                      Invite code:{" "}
                      <button
                        className="font-mono font-medium text-foreground underline underline-offset-2"
                        onClick={() => copy(ctx.team.inviteCode, "Invite code")}
                        data-testid="button-copy-code"
                      >
                        {ctx.team.inviteCode}
                      </button>{" "}
                      (grants {ctx.team.inviteCodeRole})
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Pending invitations */}
            {ctx.isAdmin && invitations && invitations.length > 0 && (
              <Card data-testid="card-pending-invites">
                <CardHeader>
                  <CardTitle className="text-lg">Pending invitations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {invitations.map((inv) => (
                    <div
                      key={inv._id}
                      className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
                      data-testid={`row-invite-${inv._id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {inv.email || "Link invite"}
                        </div>
                        <div className="text-xs text-muted-foreground capitalize">
                          {inv.role}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            copy(
                              `${window.location.origin}/join?token=${inv.token}`,
                              "Invite link",
                            )
                          }
                          data-testid={`button-copy-invite-${inv._id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            guardedAction(
                              () => revokeInvitation({ userId, invitationId: inv._id }),
                              "Couldn't revoke",
                            )
                          }
                          data-testid={`button-revoke-${inv._id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Members */}
            <Card data-testid="card-members">
              <CardHeader>
                <CardTitle className="text-lg">Members</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(members || []).map((m) => {
                  const isSelf = m.userId === userId;
                  const isOwnerRow = m.role === "owner";
                  const canManage = ctx.isAdmin && !isOwnerRow && !isSelf;
                  return (
                    <div
                      key={m._id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-border p-3"
                      data-testid={`row-member-${m.userId}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={m.profileImageUrl} />
                          <AvatarFallback>
                            {(m.name || m.email || "?").charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate" data-testid={`text-member-name-${m.userId}`}>
                            {m.name || m.email}{isSelf ? " (you)" : ""}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={STATUS_VARIANT[m.accessStatus as AccessStatus]} data-testid={`badge-status-${m.userId}`}>
                          {STATUS_LABEL[m.accessStatus as AccessStatus]}
                        </Badge>
                        {isOwnerRow ? (
                          <Badge variant="outline">Owner</Badge>
                        ) : canManage ? (
                          <>
                            <Select
                              value={m.role}
                              onValueChange={(v) =>
                                guardedAction(
                                  () =>
                                    setMemberRole({
                                      userId,
                                      targetUserId: m.userId,
                                      role: v as Role,
                                    }),
                                  "Couldn't change role",
                                )
                              }
                            >
                              <SelectTrigger className="w-28 h-8" data-testid={`select-role-${m.userId}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <Select
                              value={m.accessStatus}
                              onValueChange={(v) =>
                                guardedAction(
                                  () =>
                                    setMemberAccessStatus({
                                      userId,
                                      targetUserId: m.userId,
                                      status: v as AccessStatus,
                                    }),
                                  "Couldn't change access",
                                )
                              }
                            >
                              <SelectTrigger className="w-32 h-8" data-testid={`select-status-${m.userId}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="locked">Locked</SelectItem>
                                <SelectItem value="suspended">Suspended</SelectItem>
                                <SelectItem value="inactive">Inactive</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                guardedAction(
                                  () => removeMember({ userId, targetUserId: m.userId }),
                                  "Couldn't remove member",
                                )
                              }
                              data-testid={`button-remove-${m.userId}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <Badge variant="outline" className="capitalize">{m.role}</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Team settings (admins) */}
            {ctx.isAdmin && (
              <Card data-testid="card-team-settings">
                <CardHeader>
                  <CardTitle className="text-lg">Team settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Members can invite</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow non-admin members to send invitations.
                      </p>
                    </div>
                    <Switch
                      checked={ctx.team.membersCanInvite}
                      onCheckedChange={(v) =>
                        guardedAction(
                          () => updateTeamSettings({ userId, membersCanInvite: v }),
                          "Couldn't update",
                        )
                      }
                      data-testid="switch-members-can-invite"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Invite link enabled</Label>
                      <p className="text-sm text-muted-foreground">
                        Anyone with the link/code can join.
                      </p>
                    </div>
                    <Switch
                      checked={ctx.team.inviteCodeEnabled}
                      onCheckedChange={(v) =>
                        guardedAction(
                          () => updateTeamSettings({ userId, inviteCodeEnabled: v }),
                          "Couldn't update",
                        )
                      }
                      data-testid="switch-invite-enabled"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label>Link grants role</Label>
                      <p className="text-sm text-muted-foreground">
                        Role assigned when joining via link/code.
                      </p>
                    </div>
                    <Select
                      value={ctx.team.inviteCodeRole}
                      onValueChange={(v) =>
                        guardedAction(
                          () => updateTeamSettings({ userId, inviteCodeRole: v as Role }),
                          "Couldn't update",
                        )
                      }
                    >
                      <SelectTrigger className="w-32" data-testid="select-link-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() =>
                      guardedAction(async () => {
                        await updateTeamSettings({ userId, regenerateInviteCode: true });
                        toast({ title: "New invite code generated" });
                      }, "Couldn't regenerate")
                    }
                    data-testid="button-regenerate-code"
                  >
                    Regenerate invite code
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
